import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useFieldArray, useForm } from "react-hook-form"
import { Link, useNavigate, useParams } from "react-router-dom"
import * as z from "zod"
import { gql } from "~/__generated__"
import { getMetaContentMaybe } from "~/common/get-meta-content"
import { myLettermemeManagePath, settingsPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { ScreenLoading } from "~/ui/screen-loading"
import { Textarea } from "~/ui/textarea"
import { useToast } from "~/ui/use-toast"

const lettermemeQuery = gql(/* GraphQL */ `
  query LettermemeTrackingCodes($id: ID!) {
    lettermeme(id: $id) {
      id
      name
      urlPath
      trackingCodes {
        id
        nickname
        code
      }
      latestNewsletter {
        id
      }
    }
  }
`)

const updateTrackingCodeMutation = gql(/* GraphQL */ `
  mutation UpdateLettermemeTrackingCodes($input: LettermemeUpdateTrackingCodesInput!) {
    lettermemeUpdateTrackingCodes(input: $input) {
      lettermeme {
        id
        trackingCodes {
          id
          nickname
          code
        }
      }
    }
  }
`)

const trackingCodeEntrySchema = z
  .object({
    id: z.string().optional(),
    nickname: z.string().max(80, { message: "Nickname must be 80 characters or less" }),
    code: z
      .string()
      .max(1000, { message: "Tracking code must be 1000 characters or less" })
      .optional()
      .or(z.literal("")),
  })
  .superRefine((value, ctx) => {
    const nickname = (value.nickname || "").trim()
    const code = (value.code || "").trim()

    if (nickname.length === 0 && code.length === 0) {
      return
    }

    if (nickname.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nickname"],
        message: "Nickname is required",
      })
    }

    if (code.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["code"],
        message: "Tracking code is required",
      })
    }
  })

const trackingCodeFormSchema = z.object({
  trackingCodes: z.array(trackingCodeEntrySchema),
})

type TrackingCodeFormValues = z.infer<typeof trackingCodeFormSchema>

const createEmptyTrackingCode = () => ({ nickname: "", code: "" })

export const LettermemeTrackingCodeScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const navigate = useNavigate()

  const { data, error, loading } = useQuery(lettermemeQuery, {
    variables: { id: id! },
  })
  const lettermeme = data?.lettermeme

  useDocumentTitle(data?.lettermeme ? `${data.lettermeme.name} - Tracking Codes` : "Tracking Codes")

  const form = useForm<TrackingCodeFormValues>({
    resolver: zodResolver(trackingCodeFormSchema),
    defaultValues: {
      trackingCodes: lettermeme?.trackingCodes?.length
        ? lettermeme.trackingCodes.map((tc) => ({
            id: tc.id,
            nickname: tc.nickname || "",
            code: tc.code,
          }))
        : [createEmptyTrackingCode()],
    },
    values: lettermeme
      ? {
          trackingCodes: lettermeme.trackingCodes.length
            ? lettermeme.trackingCodes.map((tc) => ({
                id: tc.id,
                nickname: tc.nickname || "",
                code: tc.code,
              }))
            : [createEmptyTrackingCode()],
        }
      : undefined,
  })

  const trackingCodesArray = useFieldArray({
    control: form.control,
    name: "trackingCodes",
  })

  const [updateTrackingCode, mutationResult] = useSafeMutation(updateTrackingCodeMutation, {
    onCompleted: (result) => {
      const updatedCodes = result?.lettermemeUpdateTrackingCodes?.lettermeme?.trackingCodes ?? []

      form.reset({
        trackingCodes: updatedCodes.length
          ? updatedCodes.map((trackingCode) => ({
              id: trackingCode.id,
              nickname: trackingCode.nickname,
              code: trackingCode.code,
            }))
          : [createEmptyTrackingCode()],
      })
      toast({
        title: "Updated!",
        description: "Tracking codes have been updated successfully.",
      })
    },
  })
  const { loading: updateLoading } = mutationResult

  useFormErrors(form.setError, mutationResult)

  const onSubmit = (values: TrackingCodeFormValues) => {
    const payload = values.trackingCodes
      .map((tc) => ({
        id: tc.id,
        nickname: tc.nickname.trim(),
        code: (tc.code || "").trim(),
      }))
      .filter((tc) => tc.code.length > 0)

    updateTrackingCode({
      variables: {
        input: {
          id: id!,
          trackingCodes: payload,
        },
      },
    })
  }

  const handleRemoveTrackingCode = (index: number) => {
    if (trackingCodesArray.fields.length === 1) {
      form.setValue(`trackingCodes.${index}.nickname`, "", {
        shouldDirty: true,
      })
      form.setValue(`trackingCodes.${index}.code`, "", { shouldDirty: true })
      form.clearErrors([`trackingCodes.${index}.nickname`, `trackingCodes.${index}.code`])
      return
    }

    trackingCodesArray.remove(index)
  }

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <h1 className="mb-4 text-2xl font-bold">Lettermeme not found</h1>
        <p>The lettermeme you're looking for doesn't exist.</p>
      </div>
    )
  }

  const baseUrl = (getMetaContentMaybe("ORIGIN") || window.location.origin).replace(/\/$/, "")
  const urls = {
    latest: `${baseUrl}/${lettermeme.urlPath}`,
    landing: `${baseUrl}/${lettermeme.urlPath}/landing`,
    confirmation: `${baseUrl}/${lettermeme.urlPath}/subscribed`,
    archive: lettermeme.latestNewsletter
      ? `${baseUrl}/${lettermeme.urlPath}/archives/${lettermeme.latestNewsletter.id}`
      : null,
  }

  return (
    <div className="mx-auto mt-10 max-w-3xl p-8">
      <div className="mb-8">
        <Link
          to={myLettermemeManagePath({ id: lettermeme.id })}
          className="mb-3 inline-block text-gray-400"
        >
          ← back to lettermeme settings
        </Link>
        <h1 className="text-2xl font-bold">Tracking Codes</h1>
        <p className="text-gray-400">Configure analytics tracking for {lettermeme.name}</p>
      </div>

      <div className="mb-6 rounded-lg bg-gray-800 p-4">
        <p className="mb-3 text-sm text-gray-300">
          Paste your HTML tracking code snippet here. The code will be validated for security before
          saving.
        </p>
        <p className="mb-2 text-sm text-gray-400">
          Supported platforms: Google Analytics, Google Tag Manager, Facebook Pixel, TikTok Pixel,
          LinkedIn Insight Tag, X (Twitter) Analytics, Plausible, Cloudflare Web Analytics, Segment,
          and Hotjar.
        </p>
        <p className="text-sm text-gray-500">
          Note: Only HTTPS URLs and trusted tracking domains are allowed. Event handlers and
          dangerous code patterns will be rejected.
        </p>
        <div className="mt-4 text-sm text-gray-400">
          <p className="mb-2">Tracking codes will be added to:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li className="font-mono">{urls.latest}</li>
            <li className="font-mono">{urls.landing}</li>
            <li className="font-mono">{urls.confirmation}</li>
            {urls.archive && (
              <li className="font-mono">
                {urls.archive}{" "}
                <span className="font-sans text-gray-500">
                  (example archive page; changes per newsletter, e.g.{" "}
                  {lettermeme.latestNewsletter?.id ?? "<newsletter_id>"})
                </span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            {trackingCodesArray.fields.map((trackingCodeField, index) => (
              <div key={trackingCodeField.id} className="space-y-4 rounded-lg border p-4">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-xs text-gray-400 underline underline-offset-4 hover:text-white"
                    onClick={() => handleRemoveTrackingCode(index)}
                    disabled={updateLoading}
                  >
                    Remove
                  </button>
                </div>
                <FormField
                  control={form.control}
                  name={`trackingCodes.${index}.nickname`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nickname</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. Meta retargeting - Jan 2026"
                          disabled={updateLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`trackingCodes.${index}.code`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tracking Code</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="<script>&#10;  Paste your tracking code here&#10;</script>"
                          className="h-56 font-mono text-sm"
                          disabled={updateLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <button
              type="button"
              className="text-sm text-gray-300 underline underline-offset-4 hover:text-white"
              onClick={() => trackingCodesArray.append(createEmptyTrackingCode())}
              disabled={updateLoading}
            >
              + Add another
            </button>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={updateLoading}>
              {updateLoading ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(settingsPath({}))}
              disabled={updateLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
